import { Controller } from '@hotwired/stimulus'
import { getItem, setItem } from '../utils/local-storage'
import { showPreloader } from '../utils/preloader'

const SUGGESTIONS_DEBOUNCE = 250

// innodb_ft_min_token_size = 3: запрос короче трёх символов не попадёт ни в
// один токен индекса, отправлять его на сервер бессмысленно.
const MIN_QUERY_LENGTH = 3

const RECENT_STORAGE_KEY = 'padelmesh.recentSearches'
const RECENT_LIMIT = 5

/**
 * @file Единая строка поиска: наши сущности плюс места из справочника GeoNames.
 *
 * Выдача в одном списке: сверху наша база (города, страны, клубы, тренеры, сети, товары, статьи), ниже отдельной
 * группой — места из справочника (тип place).
 *
 * Выбор такого места сабмитит форму с его координатами — по ним страница результатов показывает ближайшие клубы
 * для города, которого у нас нет.
 */
export default class SearchFormController extends Controller {
  static targets = ['input']

  static values = {
    loadingLabel: String,
    suggestionsUrl: String,
    context: String,
    recentLabel: String,
    placesLabel: String
  }

  #timer = null
  #abortController = null
  #requestId = 0
  #dropdown = null
  #entries = []
  #ourEntries = []
  #placeEntries = []
  #heading = null
  #activeIndex = -1
  #onKeydown = null
  #onBlur = null

  connect() {
    if (!this.hasInputTarget) return

    this.#onKeydown = (event) => this.#handleKeydown(event)
    this.#onBlur = () => setTimeout(() => this.#close(), 150)
    this.inputTarget.addEventListener('keydown', this.#onKeydown)
    this.inputTarget.addEventListener('blur', this.#onBlur)
  }

  disconnect() {
    clearTimeout(this.#timer)
    this.#abort()

    if (this.hasInputTarget) {
      if (this.#onKeydown)
        this.inputTarget.removeEventListener('keydown', this.#onKeydown)
      if (this.#onBlur)
        this.inputTarget.removeEventListener('blur', this.#onBlur)
    }

    this.#dropdown?.remove()
  }

  changed(event) {
    // Ошибка «обязательное поле» показывается только по сабмиту — как только
    // человек редактирует поле, прячем её.
    this.#inputController(event.target)?.reset()

    // Правка строки руками отменяет координаты ранее выбранного места: иначе новая строка ушла бы с чужой точкой.
    this.#clearCoordinates()

    this.#schedule(event.target.value.trim())
  }

  // Пустое поле в фокусе — показываем, куда человек уже ходил. Это единственная
  // осмысленная подсказка, пока он ничего не ввёл.
  focused(event) {
    if (event.target.value.trim() !== '') return

    this.#renderRecent()
  }

  // Чип популярной локации: подставляем название и сразу ищем, а не уходим на
  // страницу — выбор всё равно за человеком.
  selectChip(event) {
    const location = event.currentTarget.dataset.location
    if (!location || !this.hasInputTarget) return

    this.inputTarget.value = location
    this.inputTarget.focus()
    // Как и правка руками: чип задаёт новый запрос, координаты прошлого места к нему не относятся.
    this.#clearCoordinates()
    this.#schedule(location)
  }

  submit(event) {
    const field = this.element.querySelector('input[data-input-target="input"]')

    if (field && field.value.trim() === '') {
      event.preventDefault()
      this.#inputController(field)?.validate()
      field.focus()
      return
    }

    showPreloader({ label: this.loadingLabelValue })
  }

  #inputController(field) {
    const inputElement = field.closest('[data-controller~="input"]')
    if (!inputElement) return null

    return this.application.getControllerForElementAndIdentifier(
      inputElement,
      'input'
    )
  }

  #schedule(query) {
    clearTimeout(this.#timer)

    if (query.length < MIN_QUERY_LENGTH) {
      this.#abort()

      // Пустое поле — показываем недавние переходы; недобранная строка просто закрывает выпадашку.
      if (query === '') {
        this.#renderRecent()
      } else {
        this.#close()
      }

      return
    }

    this.#timer = setTimeout(() => this.#fetch(query), SUGGESTIONS_DEBOUNCE)
  }

  // Предыдущий запрос именно обрываем, а не просто игнорируем его ответ: при
  // поиске по семи сущностям брошенная работа стоит и серверу, и сети.
  #abort() {
    this.#abortController?.abort()
    this.#abortController = null
    this.#requestId++
  }

  async #fetch(query) {
    this.#abort()
    this.#abortController = new AbortController()
    const requestId = ++this.#requestId

    // Заголовок «Недавние» относится только к истории. Путь «фокус на пустом
    // поле → ввод трёх символов» не проходит через #close, поэтому без сброса
    // живая выдача рисовалась бы под чужим заголовком.
    this.#heading = null
    const items = await this.#requestItems(query)
    if (requestId !== this.#requestId) return

    this.#ourEntries = items.filter((item) => item.type !== 'place')
    this.#placeEntries = items.filter((item) => item.type === 'place')
    this.#render()
  }

  async #requestItems(query) {
    const url = new URL(this.suggestionsUrlValue, window.location.origin)
    url.searchParams.set('q', query)
    if (this.hasContextValue) url.searchParams.set('context', this.contextValue)

    try {
      const response = await fetch(url, {
        signal: this.#abortController.signal,
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      if (!response.ok)
        throw new Error(`Suggestions failed: ${response.status}`)

      const data = await response.json()

      return Array.isArray(data.items) ? data.items : []
    } catch {
      // Наша же отмена (AbortError) или сбой сети — в обоих случаях подсказок просто нет.
      return []
    }
  }

  #renderRecent() {
    const recent = this.#readRecent()

    this.#ourEntries = recent
    this.#placeEntries = []
    this.#heading = recent.length > 0 ? this.recentLabelValue : null
    this.#render()
  }

  #render() {
    const dropdown = this.#ensureDropdown()
    const sections = []

    if (this.#ourEntries.length > 0)
      sections.push({ heading: this.#heading, entries: this.#ourEntries })
    if (this.#placeEntries.length > 0)
      sections.push({
        heading: this.placesLabelValue,
        entries: this.#placeEntries
      })

    this.#entries = sections.flatMap((section) => section.entries)
    if (this.#entries.length === 0) {
      this.#close()
      return
    }

    dropdown.innerHTML = ''
    this.#activeIndex = -1

    let index = 0
    sections.forEach((section) => {
      if (section.heading)
        dropdown.appendChild(this.#buildHeading(section.heading))
      section.entries.forEach((entry) => {
        dropdown.appendChild(this.#buildItem(entry, index))
        index += 1
      })
    })

    dropdown.style.display = 'block'
    this.inputTarget.setAttribute('aria-expanded', 'true')
  }

  #buildHeading(text) {
    const title = document.createElement('p')
    title.className = 'search-suggestions__heading'
    title.textContent = text

    return title
  }

  #buildItem(entry, index) {
    const item = document.createElement('a')
    item.className = 'search-suggestions__item'
    item.href = entry.url || '#'
    item.dataset.type = entry.type || ''
    item.setAttribute('role', 'option')
    item.setAttribute('aria-selected', 'false')

    item.appendChild(this.#buildVisual(entry))

    const body = document.createElement('span')
    body.className = 'search-suggestions__body'

    const name = document.createElement('span')
    name.className = 'search-suggestions__name'
    name.textContent = entry.name
    body.appendChild(name)

    if (entry.subtitle) {
      const subtitle = document.createElement('span')
      subtitle.className = 'search-suggestions__subtitle'
      subtitle.textContent = entry.subtitle
      body.appendChild(subtitle)
    }

    item.appendChild(body)

    // mousedown до blur: без preventDefault поле теряет фокус раньше клика, и
    // выпадашка успевает закрыться.
    item.addEventListener('mousedown', (event) => event.preventDefault())
    item.addEventListener('click', (event) => {
      event.preventDefault()
      this.#select(index)
    })

    return item
  }

  // Иконка типа слева — единственное, что отличает локацию от клуба и тренера.
  // Спрайт уже в разметке страницы (layouts/_icons_sprite), поэтому достаточно
  // ссылки на символ.
  #buildVisual(entry) {
    if (entry.thumbnail_url) {
      const image = document.createElement('img')
      image.className = 'search-suggestions__image'
      image.src = entry.thumbnail_url
      image.alt = ''
      image.width = 40
      image.height = 40
      return image
    }

    const wrapper = document.createElement('span')
    wrapper.className = 'search-suggestions__icon'
    wrapper.setAttribute('aria-hidden', 'true')
    wrapper.innerHTML = `<svg width="1em" height="1em" fill="none"><use href="#icon-${entry.icon || 'search'}"></use></svg>`

    return wrapper
  }

  #ensureDropdown() {
    if (this.#dropdown) return this.#dropdown

    const container =
      this.inputTarget.closest('.input') || this.inputTarget.parentElement
    if (getComputedStyle(container).position === 'static')
      container.style.position = 'relative'

    const dropdown = document.createElement('div')
    dropdown.className = 'search-suggestions'
    dropdown.setAttribute('role', 'listbox')
    dropdown.style.display = 'none'
    container.appendChild(dropdown)

    this.#dropdown = dropdown
    return dropdown
  }

  #close() {
    this.#entries = []
    this.#ourEntries = []
    this.#placeEntries = []
    this.#heading = null
    this.#activeIndex = -1

    if (this.#dropdown) {
      this.#dropdown.style.display = 'none'
      this.#dropdown.innerHTML = ''
    }
    if (this.hasInputTarget)
      this.inputTarget.setAttribute('aria-expanded', 'false')
  }

  #handleKeydown(event) {
    if (this.#entries.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.#moveActive(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.#moveActive(-1)
        break
      case 'Enter':
        if (this.#activeIndex >= 0) {
          event.preventDefault()
          this.#select(this.#activeIndex)
        }
        break
      case 'Escape':
        this.#close()
        break
    }
  }

  #moveActive(delta) {
    const count = this.#entries.length
    this.#activeIndex = (this.#activeIndex + delta + count) % count

    const items =
      this.#dropdown?.querySelectorAll('.search-suggestions__item') || []
    items.forEach((item, index) => {
      const active = index === this.#activeIndex
      item.classList.toggle('search-suggestions__item--active', active)
      item.setAttribute('aria-selected', active ? 'true' : 'false')
    })
  }

  #select(index) {
    const entry = this.#entries[index]
    if (!entry) return

    clearTimeout(this.#timer)
    this.#abort()

    // Своей страницы у места из справочника нет — уходим на выдачу поиска с его координатами, по ним сервер
    // показывает ближайшие клубы. В поле — «Имя, Страна»: по стране сервер отличит тёзку и запишет её в заявку на клуб.
    if (entry.type === 'place') {
      this.inputTarget.value = entry.query
      this.#close()
      this.#setCoordinates(entry)
      this.element.requestSubmit()
      return
    }

    if (!entry.url) return

    this.#rememberRecent(entry)
    this.#close()
    Turbo.visit(entry.url)
  }

  /**
   * Координаты живут в полях, которые создаются только при выборе места из справочника. Постоянные скрытые поля
   * GET-форма отправляла бы всегда, и в адресе висело бы `latitude=&longitude=` даже там, где место никто не выбирал.
   */
  #setCoordinates({ latitude, longitude }) {
    this.#clearCoordinates()
    this.element.appendChild(this.#coordinateField('latitude', latitude))
    this.element.appendChild(this.#coordinateField('longitude', longitude))
  }

  #coordinateField(name, value) {
    const field = document.createElement('input')
    field.type = 'hidden'
    field.name = name
    field.value = value
    field.dataset.searchFormCoordinate = 'true'

    return field
  }

  #clearCoordinates() {
    this.element
      .querySelectorAll('[data-search-form-coordinate]')
      .forEach((field) => field.remove())
  }

  // Недавние переходы держим в localStorage, а не на сервере: это личная
  // история, она нужна только в этом браузере и не стоит своей таблицы.
  //
  // Через getItem/setItem, а не напрямую: обёртка проверяет согласие на
  // функциональные cookie и без него молча ничего не пишет.
  #readRecent() {
    try {
      const stored = JSON.parse(getItem(RECENT_STORAGE_KEY) || '[]')

      return Array.isArray(stored) ? stored.slice(0, RECENT_LIMIT) : []
    } catch {
      return []
    }
  }

  #rememberRecent(entry) {
    try {
      const stored = this.#readRecent().filter((item) => item.url !== entry.url)
      stored.unshift({
        type: entry.type,
        icon: entry.icon,
        name: entry.name,
        subtitle: entry.subtitle,
        url: entry.url
      })

      setItem(RECENT_STORAGE_KEY, JSON.stringify(stored.slice(0, RECENT_LIMIT)))
    } catch {
      // Приватный режим и переполненное хранилище не должны ломать переход.
    }
  }
}
